"use client";

import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";
import dynamic from "next/dynamic";
import type { LottieRefCurrentProps } from "lottie-react";
import {
  recolorLottieBlack,
  hexToLottieRgba,
  type LottieRgba,
} from "@/lib/lottie-recolor";

/** lottie-web touches `document` — must not SSR (Next.js + lottie-react guidance). */
const Lottie = dynamic(() => import("lottie-react"), {
  ssr: false,
  loading: () => null,
});

/** Lottie-style color filter entries; merged into JSON via `recolorLottieBlack` (lottie-web has no native `colorFilters`). */
export type LottieColorFilter = { keypath: string; color: string };

interface LottieIconProps {
  animationData: object;
  size?: number;
  className?: string;
  /** Play once when mounted. Default: false */
  playOnMount?: boolean;
  /** Play once on hover, then stop. Default: true */
  playOnHover?: boolean;
  /** Loop the animation while hovered. Default: false */
  loopOnHover?: boolean;
  /** Speed multiplier. Default: 1 */
  speed?: number;
  /** Replace default black strokes in the Lottie JSON */
  tintStroke?: LottieRgba;
  /** Replace default black fills in the Lottie JSON */
  tintFill?: LottieRgba;
  /** Single hex tint for monochrome icons (applies to stroke + fill via recolor). */
  iconColor?: string;
  /**
   * Optional color filter descriptors (e.g. `[{ keypath: "**", color: "#4A90D9" }]`).
   * lottie-web does not apply these at runtime; the same colors are merged into the
   * animation via `recolorLottieBlack` when `iconColor` / filters are present.
   */
  colorFilters?: LottieColorFilter[];
  /**
   * When set, hover play/reset is driven by this flag (e.g. full sidebar row hover).
   * On true: play from frame 0. On false: stop and `goToAndStop(0)`.
   */
  parentHovered?: boolean;
}

/**
 * LottieIcon — premium hover-activated Lottie animation wrapper.
 * Plays the animation forward on mouse-enter, stops/resets on mouse-leave.
 * Designed for sidebar nav items and interactive buttons.
 */
const LottieIcon = React.memo(function LottieIcon({
  animationData,
  size = 20,
  className = "",
  playOnMount = false,
  playOnHover = true,
  loopOnHover = false,
  speed = 1,
  tintStroke,
  tintFill,
  iconColor,
  colorFilters,
  parentHovered,
}: LottieIconProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [isHovering, setIsHovering] = useState(false);
  const parentControlled = typeof parentHovered === "boolean";

  const rgbaFromIconOrFilters = useMemo(() => {
    if (iconColor) return hexToLottieRgba(iconColor);
    const first = colorFilters?.[0]?.color;
    if (first) return hexToLottieRgba(first);
    return null;
  }, [iconColor, colorFilters]);

  const coloredData = useMemo(() => {
    if (rgbaFromIconOrFilters) {
      return recolorLottieBlack(
        animationData,
        rgbaFromIconOrFilters,
        rgbaFromIconOrFilters,
      );
    }
    if (tintStroke && tintFill) {
      return recolorLottieBlack(animationData, tintStroke, tintFill);
    }
    return animationData;
  }, [animationData, rgbaFromIconOrFilters, tintStroke, tintFill]);

  const handleMouseEnter = useCallback(() => {
    if (!playOnHover || parentControlled) return;
    setIsHovering(true);
    const lottie = lottieRef.current;
    if (!lottie) return;
    lottie.setSpeed(speed);
    lottie.goToAndPlay(0, true);
  }, [playOnHover, parentControlled, speed]);

  const handleMouseLeave = useCallback(() => {
    if (!playOnHover || parentControlled) return;
    setIsHovering(false);
    if (!loopOnHover) {
      const lottie = lottieRef.current;
      if (!lottie) return;
      lottie.stop();
      lottie.goToAndStop(0, true);
    }
  }, [playOnHover, parentControlled, loopOnHover]);

  useEffect(() => {
    if (!playOnMount) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const lottie = lottieRef.current;
        if (!lottie) return;
        lottie.setSpeed(speed);
        lottie.goToAndPlay(0, true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [playOnMount, coloredData, speed]);

  useEffect(() => {
    if (!parentControlled || !playOnHover) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const lottie = lottieRef.current;
        if (!lottie) return;
        if (parentHovered) {
          setIsHovering(true);
          lottie.setSpeed(speed);
          lottie.goToAndPlay(0, true);
        } else {
          setIsHovering(false);
          lottie.stop();
          lottie.goToAndStop(0, true);
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [parentControlled, parentHovered, playOnHover, speed]);

  const loopActive =
    loopOnHover && (parentControlled ? parentHovered : isHovering);

  const selfHoverHandlers = parentControlled
    ? {}
    : {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      };

  return (
    <span
      {...selfHoverHandlers}
      style={{ display: "inline-flex", width: size, height: size, flexShrink: 0 }}
      className={className}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={coloredData}
        loop={loopActive}
        autoplay={false}
        style={{ width: size, height: size }}
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
      />
    </span>
  );
});

export default LottieIcon;
